import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CameraIcon, 
  VideoCameraIcon,
  PhoneIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  LockClosedIcon,
  CurrencyRupeeIcon,
  HomeIcon
} from '@heroicons/react/24/outline';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { createOpportunity } from '../store/slices/opportunitiesSlice';

import { CreateOpportunityRequest } from '../types';
import Button from '../components/common/Button';
import BackButton from '../components/common/BackButton';
import toast from 'react-hot-toast';
import LocationPicker from '../components/common/LocationPicker';
import { fetchUserRoleSKills } from '../store/slices/genericSlice';
import SkillSelector from '../components/common/SkillSelector';
import { SKILL_CATEGORIES } from '../data/skillCategories';
import { compressImage } from '../utils/compression';
import SuccessModal from '../components/common/SuccessModal';
import { OPPORTUNITY_TYPES as OPP_CONSTANTS } from '../constants/opportunities';

const OPPORTUNITY_TYPES = [
  { value: OPP_CONSTANTS.PHOTOGRAPHER, label: 'Looking for Photographer', icon: CameraIcon, recipients: 'creator' },
  { value: OPP_CONSTANTS.VIDEOGRAPHER, label: 'Looking for Videographer', icon: VideoCameraIcon, recipients: 'creator' },
  { value: OPP_CONSTANTS.BOTH_CREATORS, label: 'Looking for Photographer & Videographer', icon: '📷', recipients: 'creator' },
  { value: OPP_CONSTANTS.MODEL, label: 'Looking for Model', icon: '👤' , recipients: 'model'},
  { value: OPP_CONSTANTS.BOTH, label: 'Looking for Both (Creator & Model)', icon: '👥' , recipients: 'creator,model'},
  { value: OPP_CONSTANTS.CREATIVE_COLLABORATION, label: 'Creative Collaboration', icon: '🎨' , recipients: 'all'},
];

const EXPERIENCE_LEVELS = [
  { value: 'any', label: 'Any Level' },
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'professional', label: 'Professional' },
];

const COMPENSATION_TYPES = [
  { value: 'paid', label: 'Paid' },
  { value: 'tfp', label: 'TFP (Time for Prints)' },
  { value: 'negotiable', label: 'Negotiable' },
  { value: 'unpaid', label: 'Unpaid/Volunteer' },
];



const PostOpportunityPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { submitting } = useAppSelector((state) => state.opportunities);
  const { skills } = useAppSelector((state) => state.generic);
  

  const [currentStep, setCurrentStep] = useState(1);
  
  const [formData, setFormData] = useState<CreateOpportunityRequest>({
    title: '',
    description: '',
    opportunity_type: OPP_CONSTANTS.PHOTOGRAPHER,
    location: '',
    start_date: '',
    end_date: '',
    is_flexible_dates: false,
    duration: '',
    budget: undefined,
    is_paid: true,
    compensation_type: 'tfp',
    experience_level: 'any',
    tags: [],
    images: [], // This will store uploaded image URLs
    requirements: '',
    contact_email: user?.email || '',
    contact_phone: '',
    is_public: true,
    status: 'active',
    latitude: 0,
    longitude: 0
  });

  const [imageFiles, setImageFiles] = useState<File[]>([]); // Store actual files
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]); // For preview
  const [postRecipients, setPostRecipients] = useState<string>("all");
  const [selectedSkills, setSelectedSkills] = useState<number[] | []>([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);


  const handleInputChange = (field: keyof CreateOpportunityRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

    useEffect(() => {
      dispatch(fetchUserRoleSKills(postRecipients))
    }, [postRecipients, dispatch]);


  // const handleTagAdd = (tag: string) => {
  //   if (tag && !formData.tags.includes(tag)) {
  //     setFormData(prev => ({
  //       ...prev,
  //       tags: [...prev.tags, tag]
  //     }));
  //   }
  //   setTagInput('');
  // };

  const handleTagRemove = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  // Simplified image upload - just store files locally for preview
  const handleImageUpload = async (files: FileList) => {
    if (files.length === 0) return;

    const allFiles = Array.from(files);

    const compressedFiles = await Promise.all(
        allFiles.map(async (file) => {
            try {
                return await compressImage(file);
            } catch (error) {
                console.error("Compression failed", error);
                return file;
            }
        })
    );

    const newFiles = compressedFiles;

    if (newFiles.length === 0) return;

    const totalFiles = imageFiles.length + newFiles.length;

    if (totalFiles > 5) {
      toast.error('Maximum 5 images allowed');
      return;
    }

    // Add files to state for form submission
    setImageFiles(prev => [...prev, ...newFiles]);
    
    // Create preview URLs
    const newPreviewUrls = newFiles.map(file => URL.createObjectURL(file));
    setImagePreviewUrls(prev => [...prev, ...newPreviewUrls]);

    toast.success('Images added successfully');
  };

  // Remove image function
  const handleImageRemove = (index: number) => {
    // Remove from preview URLs
    const newPreviewUrls = imagePreviewUrls.filter((_, i) => i !== index);
    setImagePreviewUrls(newPreviewUrls);

    // Remove from files
    const newFiles = imageFiles.filter((_, i) => i !== index);
    setImageFiles(newFiles);

    // Clean up object URL to prevent memory leaks
    if (imagePreviewUrls[index]) {
      URL.revokeObjectURL(imagePreviewUrls[index]);
    }
  };

  // Updated submit function - send everything in one FormData request
  const handleSubmit = async (isDraft = false) => {
    try {
      // Create FormData object with all form fields and images
      const submitFormData = new FormData();
      
      // Append text fields
      submitFormData.append('title', formData.title);
      submitFormData.append('description', formData.description);
      submitFormData.append('opportunity_type', formData.opportunity_type);
      submitFormData.append('location', formData.location);
      
      if (formData.start_date) submitFormData.append('start_date', formData.start_date);
      if (formData.end_date) submitFormData.append('end_date', formData.end_date);
      
      submitFormData.append('is_flexible_dates', formData.is_flexible_dates.toString());
      submitFormData.append('duration', formData.duration || '');
      
      if (formData.budget !== undefined) submitFormData.append('budget', formData.budget.toString());
      
      submitFormData.append('is_paid', formData.is_paid.toString());
      submitFormData.append('compensation_type', formData.compensation_type || '');
      submitFormData.append('experience_level', formData.experience_level || '');
      
      // Append tags as array
      selectedSkills.forEach((skill) => {
        submitFormData.append('tags[]', getSkillText(skill));
      });
      
      // Append image files
      imageFiles.forEach((file) => {
        submitFormData.append('images', file);
      });
      
      submitFormData.append('requirements', formData.requirements || '');
      submitFormData.append('contact_email', formData.contact_email || '');
      submitFormData.append('contact_phone', formData.contact_phone || '');
      submitFormData.append('is_public', formData.is_public.toString());
      submitFormData.append('latitude', formData.latitude !== undefined ? formData.latitude.toString() : "");
      submitFormData.append('longitude', formData.longitude !== undefined ? formData.longitude.toString() : "");
      submitFormData.append('status', isDraft ? 'draft' : 'active');

      // Send single request with all data
      await dispatch(createOpportunity(submitFormData)).unwrap();

      // Clean up object URLs
      imagePreviewUrls.forEach(url => URL.revokeObjectURL(url));

      // Navigation logic
      if (isDraft) {
        toast.success('Opportunity saved as draft!');
        navigate('/opportunities/my?tab=drafts');
      } else {
        toast.success('Opportunity posted successfully!');
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error('Failed to create opportunity:', error);
      toast.error('Failed to create opportunity');
    }
  };

  function getSkillText(skillId: number) {
    const skill = skills.find((skill) => skill.id === skillId);
    return skill ? skill.name : "";
  }

  // Clean up object URLs on component unmount
  const imagePreviewUrlsRef = React.useRef(imagePreviewUrls);
  useEffect(() => {
    imagePreviewUrlsRef.current = imagePreviewUrls;
  }, [imagePreviewUrls]);

  useEffect(() => {
    return () => {
      imagePreviewUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Basic Information</h2>
        
        {/* Title */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Opportunity Title *
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            placeholder="e.g., Fashion Creator Needed for Brand Shoot"
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
            required
          />
        </div>

        {/* Opportunity Type */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            What are you looking for? *
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {OPPORTUNITY_TYPES.map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => {
                  handleInputChange('opportunity_type', type.value);
                  setPostRecipients(type.recipients)
                }}
                className={`p-4 border-2 rounded-lg text-left transition-all ${
                  formData.opportunity_type === type.value
                    ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white'
                }`}
              >
                <div className="flex items-center space-x-3">
                  {typeof type.icon === 'string' ? (
                    <span className="text-2xl">{type.icon}</span>
                  ) : (
                    <type.icon className="h-6 w-6" />
                  )}
                  <span className="font-medium">{type.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Project Description *
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Describe your project in detail. Include the concept, style, location details, what you're looking for in a collaborator, etc."
            rows={6}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
          />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Schedule & Location</h2>
        
        {/* Location */}
        {/* <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Location *
          </label>
          <div className="relative">
            <MapPinIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={formData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              placeholder="City, venue, or 'Remote'"
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              required
            />
          </div>
        </div> */}

         <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Location *
                    </label>
                    <LocationPicker
                    
                        value={formData.location}
                        initialCoordinates={ {
                          lat: user?.latitude,
                          lng: user?.longitude,
                        }}
                        // onChange={(location, coordinates?: { lat: number; lng: number }) => {
                        // setValue('location', location);
                        // setValue('latitude', coordinates?.lat);
                        // setValue('longitude', coordinates?.lng)

                        onChange={(location: any, coordinates: any) => {
                          handleInputChange('location', location)
                          handleInputChange('latitude', coordinates?.lat);
                          handleInputChange('longitude', coordinates?.lng);
                        // Optionally store coordinates if needed
                        if (coordinates) {
                            console.log('Selected coordinates:', coordinates);
                        }
                        }}
                        placeholder="Enter your city or location..."
                        className="w-full"
                    />
                </div>

        {/* Flexible Dates Toggle */}
        <div className="mb-6">
          <label className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={formData.is_flexible_dates}
              onChange={(e) => handleInputChange('is_flexible_dates', e.target.checked)}
              className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
            />
            <span className="font-medium text-gray-700 dark:text-gray-300">Dates are flexible</span>
          </label>
        </div>

        {/* Date Range */}
        {!formData.is_flexible_dates && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={formData.start_date}
                min={new Date().toISOString().split('T')[0]}
                onChange={(e) => handleInputChange('start_date', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                End Date
              </label>
              <input
                type="date"
                value={formData.end_date}
                min={formData.start_date || new Date().toISOString().split('T')[0]}
                onChange={(e) => handleInputChange('end_date', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
        )}

        {/* Duration */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Duration
          </label>
          <input
            type="text"
            value={formData.duration}
            onChange={(e) => handleInputChange('duration', e.target.value)}
            placeholder="e.g., 2 hours, Half day, 2 days"
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
          />
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Budget & Compensation</h2>
        
        {/* Paid Toggle */}
        <div className="mb-6">
          <label className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={!formData.is_paid}
              onChange={(e) => {
                const isUnpaid = e.target.checked;
                handleInputChange('is_paid', !isUnpaid);
                if (isUnpaid) {
                  handleInputChange('compensation_type', 'unpaid');
                  handleInputChange('budget', undefined);
                } else {
                  handleInputChange('compensation_type', 'paid');
                }
              }}
              className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
            />
            <span className="font-medium text-gray-700 dark:text-gray-300">This is an unpaid opportunity</span>
          </label>
        </div>

        {/* Budget */}
        {formData.is_paid && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Budget
            </label>
            <div className="relative">
              <CurrencyRupeeIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="number"
                value={formData.budget || ''}
                onChange={(e) => handleInputChange('budget', e.target.value ? parseFloat(e.target.value) : undefined)}
                placeholder="Enter amount"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
              />
            </div>
          </div>
        )}

        {/* Compensation Type */}
        {formData.is_paid && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Compensation Type *
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {COMPENSATION_TYPES.filter(t => t.value !== 'unpaid').map((type) => (
                <label
                  key={type.value}
                  className={`relative flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                    formData.compensation_type === type.value
                      ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                  }`}
                >
                  <input
                    type="radio"
                    name="compensation_type"
                    value={type.value}
                    checked={formData.compensation_type === type.value}
                    onChange={(e) => {
                      handleInputChange('compensation_type', e.target.value);
                    }}
                    className="sr-only"
                  />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{type.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Experience Level */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Experience Level Required
          </label>
          <select
            value={formData.experience_level}
            onChange={(e) => handleInputChange('experience_level', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            {EXPERIENCE_LEVELS.map((level) => (
              <option key={level.value} value={level.value}>
                {level.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Additional Details</h2>
        
        {/* Tags */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Tags & Specializations
          </label>
          
          {/* Common Tags */}
          {/* <div className="mb-3">
            <p className="text-sm text-gray-600 mb-2">Popular tags:</p>
            <div className="flex flex-wrap gap-2">
              {skills.map((tag) => (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => handleTagAdd(tag.name)}
                  disabled={formData.tags.includes(tag.name)}
                  className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                    formData.tags.includes(tag.name)
                      ? 'bg-teal-100 text-teal-700 border-teal-200 cursor-not-allowed'
                      : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'
                  }`}
                >
                  {tag.name}
                </button>
              ))}
            </div>
          </div> */}

          {/* Custom Tag Input */}
          {/* <div className="flex space-x-2">
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleTagAdd(tagInput))}
              placeholder="Add custom tag"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => handleTagAdd(tagInput)}
              disabled={!tagInput || formData.tags.includes(tagInput)}
            >
              Add
            </Button>
          </div> */}
          <SkillSelector
                selectedSkillIds={selectedSkills}
                onChange={(values) => setSelectedSkills(values)}
                options={skills}
                placeholder="Select specializations..."
                maxSkills={10}
                categories={SKILL_CATEGORIES}
              />

          {/* Selected Tags */}
          {formData.tags.length > 0 && (
            <div className="mt-3">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Selected tags:</p>
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-3 py-1 text-sm bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 rounded-full"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleTagRemove(tag)}
                      className="ml-2 text-teal-500 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-200"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Requirements */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Special Requirements
          </label>
          <textarea
            value={formData.requirements}
            onChange={(e) => handleInputChange('requirements', e.target.value)}
            placeholder="Any special equipment, wardrobe, skills, or other requirements..."
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
          />
        </div>

        {/* Images */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Reference Images
          </label>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Upload mood boards, reference photos, or examples (max 5 images)
          </p>
          
          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-gray-400 dark:hover:border-gray-500 transition-colors">
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => e.target.files && handleImageUpload(e.target.files)}
              className="hidden"
              id="image-upload"
              disabled={imageFiles.length >= 5}
            />
            <label 
              htmlFor="image-upload" 
              className={`cursor-pointer ${imageFiles.length >= 5 ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <CameraIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                {imageFiles.length >= 5 
                  ? 'Maximum 5 images reached' 
                  : 'Click to select images'
                }
              </p>
            </label>
          </div>

          {/* Preview selected images */}
          {imagePreviewUrls.length > 0 && (
            <div className="mt-4 grid grid-cols-3 md:grid-cols-5 gap-2">
              {imagePreviewUrls.map((previewUrl, index) => (
                <div key={index} className="relative group">
                  <img
                    src={previewUrl}
                    alt={`Reference ${index + 1}`}
                    className="w-full h-20 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => handleImageRemove(index)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Contact Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Contact Email
            </label>
            <div className="relative">
              <EnvelopeIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="email"
                value={formData.contact_email}
                onChange={(e) => handleInputChange('contact_email', e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Contact Phone (Optional)
            </label>
            <div className="relative">
              <PhoneIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="tel"
                value={formData.contact_phone}
                onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
              />
            </div>
          </div>
        </div>

        {/* Visibility */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Visibility
          </label>
          <div className="space-y-3">
            <label className="flex items-start space-x-3">
              <input
                type="radio"
                name="visibility"
                checked={formData.is_public}
                onChange={() => handleInputChange('is_public', true)}
                className="mt-1 text-teal-600 focus:ring-teal-500"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <GlobeAltIcon className="h-5 w-5 text-gray-400" />
                  <span className="font-medium text-gray-900 dark:text-white">Public</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Anyone can discover and apply to this opportunity
                </p>
              </div>
            </label>
            
            <label className="flex items-start space-x-3">
              <input
                type="radio"
                name="visibility"
                checked={!formData.is_public}
                onChange={() => handleInputChange('is_public', false)}
                className="mt-1 text-teal-600 focus:ring-teal-500"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <LockClosedIcon className="h-5 w-5 text-gray-400" />
                  <span className="font-medium text-gray-900 dark:text-white">Private</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Only people you share the link with can see this opportunity
                </p>
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3, 4].map((step) => (
        <React.Fragment key={step}>
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step <= currentStep
                ? 'bg-teal-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
            }`}
          >
            {step}
          </div>
          {step < 4 && (
            <div
              className={`w-16 h-1 mx-2 ${
                step < currentStep ? 'bg-teal-600' : 'bg-gray-200 dark:bg-gray-700'
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const canProceedToNextStep = () => {
    switch (currentStep) {
      case 1:
        return formData.title.trim() && formData.description.trim() && formData.opportunity_type;
      case 2:
        return formData.location.trim();
      case 3:
        return true; // All fields in step 3 are optional or have defaults
      case 4:
        return true; // All fields in step 4 are optional
      default:
        return false;
    }
  };

  return (
    <>
      <div className="w-full mx-auto p-4 md:p-6 h-full overflow-y-auto px-4 md:px-6 py-6 md:py-8 bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="mb-8">
      <div className="flex items-center space-x-4 mb-4">
          <BackButton />
          
          <div className="h-6 w-px bg-gray-300 dark:bg-gray-700"></div>
          
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm">
            <button 
              onClick={() => navigate('/dashboard')}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              <HomeIcon className="h-4 w-4" />
            </button>
            <span className="text-gray-400">/</span>
            <button 
              onClick={() => navigate('/opportunities')}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              Opportunities
            </button>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900 dark:text-white">Post New</span>
          </nav>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Post New Opportunity</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Create a posting to find the perfect collaborator for your project
        </p>
      </div>

      {/* Step Indicator */}
      {renderStepIndicator()}

      {/* Form */}
      <motion.div
        key={currentStep}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6"
      >
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}
      </motion.div>

      {/* Navigation */}
      {/* Navigation */}
      <div className="flex flex-col-reverse md:flex-row items-center justify-between gap-4 mt-8 pt-4 border-t border-gray-100 dark:border-gray-700">
        <div className="w-full md:w-auto">
          {currentStep > 1 ? (
            <Button
              variant="outline"
              onClick={() => setCurrentStep(currentStep - 1)}
              className="w-full md:w-auto"
            >
              Previous
            </Button>
          ) : (
            <div className="hidden md:block w-24" /> /* Spacer for desktop alignment */
          )}
        </div>

        <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
          <Button
            variant="ghost"
            onClick={() => handleSubmit(true)}
            disabled={submitting}
            className="w-full md:w-auto order-last md:order-first"
          >
            Save as Draft
          </Button>
          
          {currentStep < 4 ? (
            <Button
              variant="primary"
              onClick={() => setCurrentStep(currentStep + 1)}
              disabled={!canProceedToNextStep()}
              className="w-full md:w-auto"
            >
              Next
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={() => handleSubmit(false)}
              loading={submitting}
              disabled={!canProceedToNextStep()}
              className="w-full md:w-auto"
            >
              Post Opportunity
            </Button>
          )}
        </div>
      </div>
      {/* Quick Action Buttons - Show after step 1
      {currentStep > 1 && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600 mb-3">Quick actions:</p>
          <div className="flex space-x-3">
            <button
              onClick={() => navigate('/opportunities')}
              className="text-sm text-teal-600 hover:text-teal-700 flex items-center space-x-1"
            >
              <ListBulletIcon className="h-4 w-4" />
              <span>Browse Opportunities</span>
            </button>
            <button
              onClick={() => navigate('/opportunities/my')}
              className="text-sm text-teal-600 hover:text-teal-700 flex items-center space-x-1"
            >
              <DocumentTextIcon className="h-4 w-4" />
              <span>My Opportunities</span>
            </button>
          </div>
        </div>
      )} */}
    </div>

    <SuccessModal
      isOpen={showSuccessModal}
      onClose={() => setShowSuccessModal(false)}
      title="Success!"
      message="Opportunity posted successfully! Would you like to view your opportunities or continue browsing?"
      actionText="View My Opportunities"
      onAction={() => navigate('/opportunities/my')}
      secondaryActionText="Browse Others"
      onSecondaryAction={() => navigate('/opportunities')}
    />
    </>
  );
};

export default PostOpportunityPage;
